<script lang="ts">
import { defineComponent, h, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, onUpdated, ref, Teleport } from 'vue'
import { Pagination } from 'ant-design-vue'

// 2026-10-07：首行始终预留数字分页，筛选区缩窄横滚；保留独立下拉层及弹窗隔离。
const toolbarOwners = new WeakMap<HTMLElement, HTMLElement>()
export default defineComponent({
  name: 'APagination',
  inheritAttrs: false,
  setup(_, { attrs, slots }) {
    const anchor = ref<HTMLElement>()
    const panel = ref<HTMLElement>()
    const ready = ref(false)
    let frame = 0
    let active = true
    let disposed = false
    let observer: ResizeObserver | undefined
    let mutations: MutationObserver | undefined
    let host: HTMLElement | undefined
    let actionRow: HTMLElement | undefined
    let toolbar: HTMLElement | undefined
    let placeholder: HTMLElement | undefined
    const visible = (el: HTMLElement) => el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'

    function releaseToolbar() {
      if (toolbar && toolbarOwners.get(toolbar) === anchor.value) {
        toolbar.classList.remove('admin-pagination-toolbar')
        toolbar.style.removeProperty('--admin-pagination-reserve')
        toolbarOwners.delete(toolbar)
      }
      toolbar = undefined
    }
    function firstToolbar(scope: HTMLElement, source: HTMLElement) {
      // 显式列表标题优先（同一弹窗有多个绑定列表）；其他页面始终取第一条可见筛选行。
      let container = source.parentElement
      while (container && scope.contains(container)) {
        const explicit = Array.from(container.querySelectorAll<HTMLElement>('[data-pagination-toolbar]')).find(visible)
        if (explicit) return explicit
        if (container === scope) break
        container = container.parentElement
      }
      const row = Array.from(scope.querySelectorAll<HTMLElement>('.admin-list-toolbar, .query-command, .query, .guide-toolbar'))
        .find(el => visible(el) && !el.closest('.ant-table') && el.closest('.ant-modal-content, .ant-drawer-content, .c-modal-cover, #appmain') === scope)
      if (row) return row
      // 没有筛选项的弹窗/抽屉，页码仍放列表顶部，底部确认/取消按钮保持原位。
      if (!placeholder?.isConnected) {
        placeholder = document.createElement('div')
        placeholder.className = 'admin-pagination-placeholder'
        const body = scope.querySelector<HTMLElement>('.ant-modal-body, .ant-drawer-body, .c-modal-body') || scope
        body.prepend(placeholder)
      }
      return placeholder
    }

    function layout() {
      frame = 0
      const source = anchor.value, floating = panel.value
      if (!source || !floating) return
      const scope = source.closest<HTMLElement>('.ant-modal-content, .ant-drawer-content, .c-modal-cover, #appmain')
      if (!active || !scope || !visible(source) || !visible(scope)) {
        floating.style.display = 'none'
        releaseToolbar()
        return
      }
      // 弹窗叠加时隐藏底层分页，关闭上层弹窗后恢复，避免独立浮层穿透遮罩。
      const drawer = source.closest('.ant-drawer')
      if (drawer && !drawer.classList.contains('ant-drawer-open')) { floating.style.display = 'none'; return }
      const modalSelector = '.ant-modal-wrap, .ant-drawer-open > .ant-drawer-content-wrapper, .c-modal-cover'
      const modals = Array.from(document.querySelectorAll<HTMLElement>(modalSelector)).filter(visible)
      const owner = source.closest(modalSelector)
      const topModal = modals.sort((a, b) => (Number(getComputedStyle(a).zIndex) || 1000) - (Number(getComputedStyle(b).zIndex) || 1000)).pop()
      if (topModal && topModal !== owner) { floating.style.display = 'none'; return }
      floating.style.display = 'block'
      floating.style.maxWidth = 'none'
      const naturalWidth = floating.getBoundingClientRect().width
      const row = firstToolbar(scope, source)
      if (toolbar !== row) { releaseToolbar(); toolbar = row }
      toolbarOwners.set(row, source)
      if (!row.classList.contains('admin-pagination-toolbar')) row.classList.add('admin-pagination-toolbar')
      const reserve = `${Math.ceil(naturalWidth) + 12}px`
      if (row.style.getPropertyValue('--admin-pagination-reserve') !== reserve) row.style.setProperty('--admin-pagination-reserve', reserve)
      const scrollScope = scope.querySelector<HTMLElement>('.ant-modal-body, .ant-drawer-body, .c-modal-body') || scope
      const viewport = scrollScope.getBoundingClientRect()
      const bounds = row.getBoundingClientRect()
      const parent = row.parentElement
      if (!parent) return
      const parentBounds = parent.getBoundingClientRect()
      const left = Math.max(8, bounds.left, viewport.left)
      const right = Math.min(window.innerWidth - 8, parentBounds.right - parseFloat(getComputedStyle(parent).paddingRight || '0'), viewport.right)
      const available = Math.max(0, right - left)
      if (!available) { floating.style.display = 'none'; return }
      floating.style.maxWidth = `${available}px`
      const pager = floating.querySelector<HTMLElement>('.ant-pagination')
      if (!pager) { floating.style.display = 'none'; source.style.height = '0px'; return }
      // 页码和条数选项不换行；左侧筛选单独横滚，不能挤占分页位置。
      const width = Math.min(naturalWidth, available)
      const height = floating.getBoundingClientRect().height
      const rowStyle = getComputedStyle(row)
      const paddingTop = parseFloat(rowStyle.paddingTop) || 0
      const contentHeight = row.clientHeight - paddingTop - (parseFloat(rowStyle.paddingBottom) || 0)
      const top = bounds.top + paddingTop + Math.max(0, (contentHeight - height) / 2)
      if (host && !host.classList.contains('admin-pagination-inline-host')) host.classList.add('admin-pagination-inline-host')
      source.style.height = '0px'
      const minimum = Math.max(8, viewport.top + 4)
      const maximum = Math.min(window.innerHeight - 8, viewport.bottom - 4) - height
      floating.style.left = `${Math.max(left, right - width)}px`
      floating.style.top = `${Math.max(minimum, Math.min(top, maximum))}px`
      floating.style.visibility = 'visible'
      floating.style.zIndex = scope.id === 'appmain' ? '20' : '1050'
      floating.dataset.inline = 'true'
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(layout)
    }
    function onScroll(event: Event) {
      // 下拉列表内部的滚动不触发布局，防止选项列表跳动。
      if (event.target instanceof Node && panel.value?.contains(event.target)) return
      schedule()
    }
    function containWheel(event: WheelEvent) {
      const target = event.target as HTMLElement
      const dropdown = target.closest('.ant-select-dropdown')
      if (!dropdown) return
      event.stopPropagation()
      const list = dropdown.querySelector<HTMLElement>('.rc-virtual-list-holder')
      if (!list || (event.deltaY < 0 && list.scrollTop <= 0) || (event.deltaY > 0 && list.scrollTop + list.clientHeight >= list.scrollHeight - 1)) event.preventDefault()
    }
    onMounted(async () => {
      const parent = anchor.value?.parentElement
      if (parent && parent.children.length === 1 && !parent.textContent?.trim()) {
        host = parent
        host.classList.add('admin-pagination-host')
      }
      if (parent && Array.from(parent.children).some(el => el.matches('.ant-btn'))) {
        actionRow = parent
        actionRow.classList.add('admin-pagination-action-row')
      }
      ready.value = true
      await nextTick()
      if (disposed) return
      observer = new ResizeObserver(schedule)
      if (anchor.value) {
        let current: HTMLElement | null = anchor.value
        while (current && current !== document.body) { observer.observe(current); current = current.parentElement }
      }
      if (panel.value) {
        observer.observe(panel.value)
        panel.value.addEventListener('wheel', containWheel, { passive: false })
      }
      window.addEventListener('resize', schedule)
      document.addEventListener('scroll', onScroll, true)
      // 2026-10-07：抽屉滑入只改变transform，不触发尺寸观察；动画结束后重新定位首行分页。
      document.addEventListener('transitionend', schedule, true)
      document.addEventListener('animationend', schedule, true)
      mutations = new MutationObserver(records => {
        if (records.some(record => record.target instanceof HTMLElement && !record.target.closest('.admin-pagination-panel, .admin-pagination-anchor'))) schedule()
      })
      mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
      schedule()
    })
    onUpdated(schedule)
    onActivated(() => { active = true; schedule() })
    onDeactivated(() => { active = false; schedule() })
    onBeforeUnmount(() => {
      active = false
      disposed = true
      cancelAnimationFrame(frame)
      observer?.disconnect()
      mutations?.disconnect()
      window.removeEventListener('resize', schedule)
      document.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('transitionend', schedule, true)
      document.removeEventListener('animationend', schedule, true)
      panel.value?.removeEventListener('wheel', containWheel)
      releaseToolbar()
      placeholder?.remove()
      host?.classList.remove('admin-pagination-host', 'admin-pagination-inline-host')
      actionRow?.classList.remove('admin-pagination-action-row')
    })
    return () => h('div', { ref: anchor, class: 'admin-pagination-anchor' }, ready.value ? [
      h(Teleport, { to: 'body' }, h('div', { ref: panel, class: 'admin-pagination-panel', style: { display: 'none' } }, [h(Pagination, { showLessItems: true, ...attrs }, slots)]))
    ] : [])
  }
})
</script>

<style lang="scss">
.admin-pagination-anchor { width: 0; height: 0; min-width: 0; flex: 0 0 0; }
.admin-pagination-action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  > .admin-pagination-anchor { width: 0; flex: 0 0 0; }
  > .ant-btn { flex: 0 0 auto; }
}
.admin-pagination-panel {
  position: fixed;
  width: max-content;
  padding: 2px 0;
  background: #fff;
  box-sizing: border-box;
  .ant-pagination { display: flex; flex-wrap: nowrap; justify-content: flex-end; align-items: center; margin: 0; white-space: nowrap; }
  .ant-pagination > li { flex: 0 0 auto; }
  .ant-pagination-options { display: flex; align-items: center; flex-wrap: nowrap; }
  .ant-pagination-options-quick-jumper { white-space: nowrap; }
  .ant-select-dropdown .rc-virtual-list-holder { overscroll-behavior: contain; }
}
// 2026-10-06：分页容器不再横滚；仅空出的纯分页行折叠，不影响同一行其他操作。
#appmain .admin-pagination-host, .ant-modal .admin-pagination-host, .ant-drawer .admin-pagination-host {
  overflow: visible;
  min-width: 0;
  max-width: 100%;
  .admin-pagination-anchor { min-width: 0; }
  &.admin-pagination-inline-host { height: 0 !important; min-height: 0; margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0; padding-bottom: 0; }
}
</style>
